import type { GridCard } from '@Components/BigBrowse/Model/Classes/GridCard';
import type { PlaneTypes } from '@Components/BigBrowse/Model/ModelTypes';
import type { MerchandiserProduct as MerchandiserProductType } from '@Components/BigBrowse/Views/DetailViews/MultiSectionView/common/MultiSectionView.types';
import { useBbContentPresenter } from '@Components/BigBrowse/bb2/BbContentPresenter';
import { Assets } from '@Utils/AssetsStore';
import { a, easings, useSpring } from '@react-spring/three';
import { observer } from 'mobx-react-lite';

import { ProductCardRect } from '../../primitives/ProductCardRect';

type Props = {
  gridCard: GridCard<PlaneTypes.Layout_Merchandiser>;
  index: number;
  animatedIn: boolean;
  zRange?: [number, number];
};

export const MerchandiserProduct = observer(function MerchandiserProduct({
  gridCard,
  index,
  animatedIn,
  zRange = [0, 0.1],
}: Props) {
  const productImage = gridCard.data!.internals!.productImages[index];

  const contentPresenter = useBbContentPresenter();
  const { dx, dy, scale } = useAnimatedStates(animatedIn, productImage);
  const opacity = animatedIn ? 1 : 0;

  const [zMin] = zRange;

  return (
    <a.group position-x={dx} position-y={dy} position-z={zMin} scale={scale}>
      <ProductCardRect
        key={index + gridCard.key!}
        position={{ x: 0, y: 0 }}
        size={productImage.size}
        imageUrl={productImage.product.imageUrl}
        uniqueKey={gridCard.key!}
        shadow={{ asset: Assets.Image_Shadow, bmpSize: 250, scale: 1.4 }}
        opacity={opacity}
        radius={productImage.radius}
        onClick={() => {
          contentPresenter.openProductPanel(productImage.product.id);
        }}
      />
    </a.group>
  );
});

const useAnimatedStates = (
  animatedIn: boolean,
  { position, animateOutPosition }: MerchandiserProductType
) =>
  useSpring({
    from: {
      dx: animatedIn ? animateOutPosition.x : position.x,
      dy: animatedIn ? animateOutPosition.y : position.y,
      scale: animatedIn ? 1.05 : 1,
    },
    to: {
      dx: animatedIn ? position.x : animateOutPosition.x,
      dy: animatedIn ? position.y : animateOutPosition.y,
      scale: animatedIn ? 1 : 1.05,
    },
    config: { duration: 1000, easing: easings.easeOutExpo },
  });
